import { useRef } from 'react'
import { motion, useInView } from 'framer-motion'

const plans = [
  {
    name: 'RESET',
    tagline: 'Per chi deve ripartire.',
    price: 'da €79/mese',
    features: [
      'Analisi iniziale',
      'Routine base',
      'Focus e stress',
      '2 check al mese',
    ],
    recommended: false,
    color: '#9ca3af',
  },
  {
    name: 'RESPAWN',
    tagline: 'Per chi vuole cambiare ritmo.',
    price: 'da €149/mese',
    features: [
      'Analisi completa',
      'Coaching settimanale',
      'Allenamento e abitudini',
      'Supporto WhatsApp',
      'Tracking progressi',
    ],
    recommended: true,
    color: '#8b5cf6',
  },
  {
    name: 'ELITE RAID',
    tagline: 'Per chi vuole performance alta.',
    price: 'da €299/mese',
    features: [
      'Coaching avanzato',
      'Neurotraining',
      'Movimento',
      'Focus profondo',
      'Strategia personale',
      'Supporto prioritario',
    ],
    recommended: false,
    color: '#00d4ff',
  },
]

export default function PricingSection() {
  const sectionRef = useRef(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })

  return (
    <section
      id="percorso"
      ref={sectionRef}
      className="relative w-full py-24 md:py-32 px-4"
      style={{ background: '#050510' }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-nerd-white mb-4">
            Scegli il tuo{' '}
            <span className="text-gradient">percorso.</span>
          </h2>
        </motion.div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {plans.map((plan, i) => (
            <motion.div
              key={plan.name}
              className={`nerd-card rounded-xl p-8 relative flex flex-col ${
                plan.recommended ? 'md:scale-105 md:z-10' : ''
              }`}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
              style={{
                borderColor: plan.recommended ? `${plan.color}50` : undefined,
              }}
            >
              {/* Recommended badge */}
              {plan.recommended && (
                <div
                  className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-[10px] font-display font-bold uppercase tracking-wider"
                  style={{
                    background: plan.color,
                    color: '#050510',
                  }}
                >
                  Consigliato
                </div>
              )}

              {/* Plan name */}
              <h3
                className="font-display text-2xl font-bold mb-1"
                style={{ color: plan.color }}
              >
                {plan.name}
              </h3>

              <p className="text-nerd-gray text-sm mb-4">{plan.tagline}</p>

              {/* Price */}
              <div className="mb-6 pb-6 border-b border-nerd-purple/10">
                <span className="font-display text-3xl font-bold text-nerd-white">
                  {plan.price}
                </span>
              </div>

              {/* Features */}
              <ul className="flex-1 space-y-3 mb-8">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3 text-sm">
                    <span
                      className="mt-0.5 w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ background: `${plan.color}20`, color: plan.color }}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="w-2.5 h-2.5">
                        <path d="M5 12l5 5L20 7" />
                      </svg>
                    </span>
                    <span className="text-nerd-gray">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <button
                className="w-full py-3 rounded-lg font-display text-sm font-semibold uppercase tracking-wider transition-all duration-300"
                style={{
                  background: plan.recommended
                    ? `linear-gradient(135deg, ${plan.color}, #00d4ff)`
                    : 'transparent',
                  color: plan.recommended ? '#050510' : plan.color,
                  border: plan.recommended ? 'none' : `1px solid ${plan.color}40`,
                }}
                onMouseEnter={(e) => {
                  if (!plan.recommended) {
                    e.currentTarget.style.background = `${plan.color}15`
                    e.currentTarget.style.borderColor = plan.color
                  }
                }}
                onMouseLeave={(e) => {
                  if (!plan.recommended) {
                    e.currentTarget.style.background = 'transparent'
                    e.currentTarget.style.borderColor = `${plan.color}40`
                  }
                }}
              >
                Avvia percorso
              </button>

              {/* Glow effect for recommended */}
              {plan.recommended && (
                <div
                  className="absolute inset-0 rounded-xl pointer-events-none opacity-30"
                  style={{
                    boxShadow: `0 0 60px ${plan.color}30, inset 0 0 30px ${plan.color}10`,
                  }}
                />
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
